import React, { useState } from 'react';
import { Card, Row, Col, Form, Button, Table, Badge } from 'react-bootstrap';
import DatePicker from 'react-datepicker';

const DailyReportCard = ({
  dailyReport,
  fetchDailyReport,
  selectedMode,
  setSelectedMode,
  selectedDate,
  setSelectedDate,
  selectedDomain,
  setSelectedDomain,
  exportToCSV,
  domains,
  selectedStartDate,
  selectedEndDate,
  setSelectedStartDate,
  setSelectedEndDate
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const data = dailyReport?.data || [];
  const totalRecords = data.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const paginatedData = data.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  // Generate smart pagination numbers
  const getPaginationRange = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    });

    return rangeWithDots;
  };

  const handleModeChange = (mode) => {
    setSelectedMode(mode);
    setCurrentPage(1);

    if (mode === 'today' || mode === 'yesterday') {
      fetchDailyReport(mode, null, selectedDomain);
    } else if (mode === 'date') {
      fetchDailyReport('date', selectedDate, selectedDomain);
    } else if (mode === 'range') {
      fetchDailyReport('range', null, selectedDomain, selectedStartDate, selectedEndDate);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedMode('date');
    setCurrentPage(1);
    fetchDailyReport('date', date, selectedDomain);
  };

  const handleStartDateChange = (date) => {
    setSelectedStartDate(date);
    if (selectedMode === 'range') {
      setCurrentPage(1);
      fetchDailyReport('range', null, selectedDomain, date, selectedEndDate);
    }
  };

  const handleEndDateChange = (date) => {
    setSelectedEndDate(date);
    if (selectedMode === 'range') {
      setCurrentPage(1);
      fetchDailyReport('range', null, selectedDomain, selectedStartDate, date);
    }
  };

  const handleDomainChange = (e) => {
    const value = e.target.value === 'All' ? '' : e.target.value;
    setSelectedDomain(value);
    setCurrentPage(1);

    if (selectedMode === 'today' || selectedMode === 'yesterday') {
      fetchDailyReport(selectedMode, null, value);
    } else if (selectedMode === 'date') {
      fetchDailyReport('date', selectedDate, value);
    } else if (selectedMode === 'range') {
      fetchDailyReport('range', null, value, selectedStartDate, selectedEndDate);
    }
  };

  const handleExport = () => {
    let filenameSuffix = dailyReport?.date || 'date';
    if (selectedMode === 'range') {
      const s = selectedStartDate instanceof Date ? selectedStartDate.toISOString().split('T')[0] : selectedStartDate;
      const e = selectedEndDate instanceof Date ? selectedEndDate.toISOString().split('T')[0] : selectedEndDate;
      filenameSuffix = `${s}_to_${e}`;
    } else if (selectedMode === 'date') {
      filenameSuffix = selectedDate instanceof Date ? selectedDate.toISOString().split('T')[0] : filenameSuffix;
    }
    exportToCSV(data, `daily_report_${filenameSuffix}.csv`);
  };

  return (
    <Card className="mb-4 shadow-sm">
      <Card.Header className="bg-primary text-white">
        <h5 className="mb-0">Daily Production Report</h5>
      </Card.Header>
      <Card.Body>
        {/* Filters Section */}
        <Row className="mb-4">
          <Col md={12} lg={4} className="mb-3">
            <Form.Group>
              <Form.Label className="fw-semibold">Date Mode</Form.Label>
              <div className="btn-group d-flex" role="group">
                <Button
                  size="sm"
                  variant={selectedMode === 'today' ? 'primary' : 'outline-primary'}
                  onClick={() => handleModeChange('today')}
                  style={{ flex: 1 }}
                >
                  Today
                </Button>
                <Button
                  size="sm"
                  variant={selectedMode === 'yesterday' ? 'primary' : 'outline-primary'}
                  onClick={() => handleModeChange('yesterday')}
                  style={{ flex: 1 }}
                >
                  Yesterday
                </Button>
                <Button
                  size="sm"
                  variant={selectedMode === 'date' ? 'primary' : 'outline-primary'}
                  onClick={() => handleModeChange('date')}
                  style={{ flex: 1 }}
                >
                  Date
                </Button>
                <Button
                  size="sm"
                  variant={selectedMode === 'range' ? 'primary' : 'outline-primary'}
                  onClick={() => handleModeChange('range')}
                  style={{ flex: 1 }}
                >
                  Range
                </Button>
              </div>
            </Form.Group>
          </Col>

          <Col md={12} lg={4} className="mb-3">
            <Form.Group>
              <Form.Label className="fw-semibold">
                {selectedMode === 'range' ? 'Select Date Range' : 'Select Date'}
              </Form.Label>
              {selectedMode !== 'range' ? (
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  className="form-control"
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Choose a date"
                />
              ) : (
                <div className="d-flex gap-2 align-items-center">
                  <div style={{ flex: 1 }}>
                    <DatePicker
                      selected={selectedStartDate}
                      onChange={handleStartDateChange}
                      className="form-control"
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Start date"
                      maxDate={selectedEndDate}
                    />
                  </div>
                  <span className="text-muted fw-bold">to</span>
                  <div style={{ flex: 1 }}>
                    <DatePicker
                      selected={selectedEndDate}
                      onChange={handleEndDateChange}
                      className="form-control"
                      dateFormat="yyyy-MM-dd"
                      placeholderText="End date"
                      minDate={selectedStartDate}
                    />
                  </div>
                </div>
              )}
            </Form.Group>
          </Col>

          <Col md={6} lg={2} className="mb-3">
            <Form.Group>
              <Form.Label className="fw-semibold">Domain</Form.Label>
              <Form.Select value={selectedDomain || 'All'} onChange={handleDomainChange}>
                {domains?.map((d, idx) => (
                  <option key={idx} value={d}>{d}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={6} lg={2} className="mb-3 d-flex align-items-end">
            <Button
              variant="success"
              onClick={handleExport}
              disabled={!data.length}
              className="w-100"
            >
              Export CSV
            </Button>
          </Col>
        </Row>

        {/* Summary Info */}
        <div className="mb-3 p-2 bg-light rounded">
          <small className="text-muted">
            Showing {paginatedData.length > 0 ? (currentPage - 1) * recordsPerPage + 1 : 0} to{' '}
            {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} records
          </small>
        </div>

        {/* Table Section */}
        <Row>
          <Col>
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Domain</th>
                    <th>Application Date</th>
                    <th>Attended</th>
                    <th>Registered</th>
                    <th>Assigned</th>
                    <th>Rejected</th>
                    <th>Left</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="text-center py-4 text-muted">
                        No records found for the selected criteria
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((row) => (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td>{row.first_name}</td>
                        <td>{row.last_name}</td>
                        <td>{row.email_id}</td>
                        <td>{row.domain}</td>
                        <td>{row.application_date ? new Date(row.application_date).toLocaleDateString('en-CA') : ''}</td>
                        <td><Badge bg={row.attended_meet_greet ? 'success' : 'secondary'}>{row.attended_meet_greet ? 'Yes' : 'No'}</Badge></td>
                        <td><Badge bg={row.registered_startupworld ? 'info' : 'secondary'}>{row.registered_startupworld ? 'Yes' : 'No'}</Badge></td>
                        <td><Badge bg={row.assigned_project ? 'warning' : 'secondary'}>{row.assigned_project ? 'Yes' : 'No'}</Badge></td>
                        <td><Badge bg={row.rejected ? 'danger' : 'secondary'}>{row.rejected ? 'Yes' : 'No'}</Badge></td>
                        <td><Badge bg={row.left ? 'dark' : 'secondary'}>{row.left ? 'Yes' : 'No'}</Badge></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            {/* Enhanced Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <div className="text-muted small">
                  Page {currentPage} of {totalPages}
                </div>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    {/* First Page */}
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        aria-label="First"
                        title="First page"
                      >
                        &laquo;
                      </button>
                    </li>

                    {/* Previous Page */}
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Previous"
                        title="Previous page"
                      >
                        &lsaquo;
                      </button>
                    </li>

                    {/* Page Numbers */}
                    {getPaginationRange().map((page, idx) => (
                      page === '...' ? (
                        <li key={`dots-${idx}`} className="page-item disabled">
                          <span className="page-link">...</span>
                        </li>
                      ) : (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        </li>
                      )
                    ))}

                    {/* Next Page */}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        aria-label="Next"
                        title="Next page"
                      >
                        &rsaquo;
                      </button>
                    </li>

                    {/* Last Page */}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        aria-label="Last"
                        title="Last page"
                      >
                        &raquo;
                      </button>
                    </li>
                  </ul>
                </nav>
                <div className="text-muted small">
                  {totalRecords} total records
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default DailyReportCard;